import {apiConstants, postGeneric, getGeneric, postMultipart} from '../../../../common/constants'
import {getMerchant, getRole} from '../../../../helpers/ReduxHelpers'
import {toListBody, excessKgOf} from './listBody'

/**
 * Weight Disputes v2 — the live-data seam.
 *
 * Everything the module knows about the backend lives in this one file: the
 * endpoint shapes (see proship_mongo's weightv2/WeightV2Controller) and the
 * DTO→row mapping. index.js decides per mount whether it is running live
 * (fetch + these actions) or seeded (the frozen demo dataset + local-only
 * transitions); nothing else in the module imports from here.
 *
 * TRANSITIONAL STRATEGY — load everything, filter locally: the whole state
 * layer (state/query.js, triage, analytics) filters/sorts/pages a full
 * in-memory array, so the integration fetches every row once (paged 200 at a
 * time, newest first) and hands the array over. Server-side paging via
 * WD_V2_LIST's tab/q/urgency/page params is the follow-up once row counts
 * demand it — the endpoint already supports it.
 */

// The API serves the canonical stored enum; this module's reference data
// (data/reference.js) and the frozen demo dataset still key the same status
// as AUTO_ACCEPTED. Translate at the boundary, both directions, so neither
// side needs to know about the other's spelling.
const STATUS_FROM_API = {AUTO_ACCEPTED_BY_SYSTEM: 'AUTO_ACCEPTED'}

const feStatus = (s) => (s && STATUS_FROM_API[s]) || s
// Dates arrive as ISO strings or epoch millis depending on serializer config;
// new Date() accepts both, and the module's own rows carry ISO strings.
const toIso = (v) => (v == null ? null : new Date(v).toISOString())

/** WdV2Row (kg DTO, see weightv2/pojo/WdV2Row.java) → this module's row shape. */
export function mapRowFromApi(r) {
  if (!r) return null
  return {
    id: r.id,
    awb: r.awb || '',
    orderId: r.orderId || '',
    sku: r.sku || '',
    product: r.product || '',
    courier: r.courier || '',
    service: r.service || '',
    originalWeight: r.originalWeight != null ? r.originalWeight : 0,
    appliedWeight: r.appliedWeight != null ? r.appliedWeight : 0,
    // Legacy WDs predate the captured dead/vol split and dims — the grid and
    // drawer already render row.dims/declaredDims defensively (RecordSections
    // falls back to zeros), so nulls pass through rather than being invented.
    declaredDims: r.declaredDims || null,
    declaredVolKg: r.declaredVolKg != null ? r.declaredVolKg : null,
    dims: r.dims || null,
    deadKg: r.deadKg != null ? r.deadKg : null,
    volKg: r.volKg != null ? r.volKg : null,
    excessKg: excessKgOf(r),
    rawExcessKg: r.excessKg != null ? r.excessKg : 0,   // the plain weight difference, before slabs
    excessAmount: r.excessAmount != null ? r.excessAmount : 0,
    status: feStatus(r.status),
    shipmentState: r.shipmentState || null,
    createdAt: toIso(r.createdAt),
    updatedAt: toIso(r.updatedAt),
    deadlineAt: toIso(r.deadlineAt),
    courierImages: r.courierImages || 0,
    merchantImages: r.merchantImages || 0,
    hasVideo: !!r.hasVideo,
    claimedDeadKg: r.claimedDeadKg != null ? r.claimedDeadKg : null,
    productCategory: r.productCategory || null,
    productUrl: r.productUrl || null,
    courierImageLinks: r.courierImageLinks || [],
    merchantImageLinks: r.merchantImageLinks || [],
    videoLink: r.videoLink || null,
    // Item 3 — only on SETTLED_PROSHIP rows: the weight Prozo settled at, what you paid for it
    // (your own rate card at that weight) and how much of the hold came back.
    settledKg: r.settledKg != null ? r.settledKg : null,
    settledCharge: r.settledCharge != null ? r.settledCharge : null,
    settledReleased: r.settledReleased != null ? r.settledReleased : null,
    statusLog: (r.statusLog || []).map((e) => ({
      status: feStatus(e.status),
      at: toIso(e.at),
      by: e.by || 'System',
      ...(e.remark ? {remark: e.remark} : {}),
    })),
  }
}

// 25 pages × 200 = 5,000 rows — far beyond any real merchant's open book.
// A hard stop, not a target: it exists so a runaway count can never turn
// this into an unbounded fetch loop.


/**
 * The merchant this panel is showing: the SESSION's merchant (getMerchant), the same id every other
 * merchant-panel module sends with its requests — the v1 Weight Disputes page, Orders, Dashboard, NDR.
 *
 * The server's own default is the wrong merchant for Prozo staff. They sign in with admin-role logins,
 * and picking a merchant (auth/SelectLogin.js) changes only the session — the token keeps their HOME
 * merchant. So a read that names no merchant answers with every merchant (the admin-wide scope) or with
 * the home merchant (reads that fall back to the token), never the one on screen. The session is also
 * right under the cross-account switch grant, which updates it (hooks/useMerchantSwitcher.js). The server
 * still RBAC-checks the id; a merchant's own login sends its own merchant and lands where it always did.
 */
export function actingMerchantOid() {
  try {
    const merchant = getMerchant()
    const oid = merchant && merchant.id
    return oid && String(oid).trim() ? String(oid).trim() : null
  } catch (err) {
    return null
  }
}

/**
 * True for a Prozo staff login (the token's role — picking a merchant does not change it). The server
 * treats a dispute or an accept from such a login as done ON THE MERCHANT'S BEHALF and refuses it
 * without a remark (WdActionAccessRules), so the dialogs ask for one up front. Same roles as the
 * server's AuthorizeService.isProshipAdmin: a super admin counts too, or the dialog would call the
 * remark optional (or, for accept, not offer one) and the server would then refuse the action.
 */
export function isInternalLogin() {
  try {
    const roles = getRole()
    return roles.includes('ROLE_PROSHIPADMIN') || roles.includes('ROLE_SUPER_ADMIN')
  } catch (err) {
    return false
  }
}

function withMerchant(body) {
  const merchantId = actingMerchantOid()
  return merchantId ? {...body, merchantId} : body
}

/**
 * ONE page of discrepancies, plus the counts that used to be derived from holding every row.
 *
 * This replaces fetchAllDisputes, which walked up to 25 pages of 200 and then filtered, sorted,
 * counted and aggregated in the browser. That is correct for a few hundred rows. The largest
 * merchant in production has 1,866,768: the panel read 5,000 of them and presented the totals as the
 * whole book, and — before the sort indexes existed — spent about two minutes per page doing it.
 *
 * `tabCounts` and `count` come back with every response, so the tab labels and the footer total are
 * the server's numbers rather than a count of what happened to be fetched.
 */
export async function fetchDisputePage(params) {
  const res = await postGeneric(apiConstants.WD_V2_LIST, withMerchant(toListBody(params)))
  const body = (res && res.data) || {}
  return {
    rows: (body.data || []).map(mapRowFromApi).filter(Boolean),
    count: body.count || 0,
    remaining: body.remaining || 0,
    tabCounts: body.tabCounts || {},
  }
}

/** Rows per request and the most rows in one download (the server enforces both: 5,000 and 25,000). */
export const EXPORT_SLICE = 5000
export const EXPORT_MAX = 25000

/**
 * Every row for the tab, filters and date range — not the page on screen (28 Oct) — fetched a slice at a time
 * through the export endpoint so no single request runs long. De-duplicated by id. Resolves { rows, capped }.
 */
export async function fetchAllDisputes(params) {
  const base = withMerchant(toListBody(params))
  delete base.page
  delete base.size
  const seen = new Set()
  const rows = []
  for (let from = 0; from < EXPORT_MAX; from += EXPORT_SLICE) {
    const res = await postGeneric(`${apiConstants.WD_V2_BASE}/export`, {...base, exportFrom: from, exportLimit: EXPORT_SLICE})
    const body = (res && res.data) || {}
    ;(body.data || []).map(mapRowFromApi).filter(Boolean).forEach((r) => { if (!seen.has(r.id)) { seen.add(r.id); rows.push(r) } })
    if (!body.remaining) return {rows, capped: false}
  }
  return {rows, capped: true}
}

/** The KPI strip, computed over the whole filtered set rather than the fetched page. */
export async function fetchSummary(params) {
  const b = withMerchant(toListBody(params))
  const res = await getGeneric(`${apiConstants.WD_V2_BASE}/summary`, dropEmpty({
    merchantId: b.merchantId, fromDate: b.fromDate, toDate: b.toDate,
  }))
  return (res && res.data) || null
}

/** Recovery Analytics: raised vs recovered by week, over the whole filtered set. */
export async function fetchWeekly(params) {
  const b = withMerchant(toListBody(params))
  const res = await getGeneric(`${apiConstants.WD_V2_BASE}/analytics/weekly`, dropEmpty({
    merchantId: b.merchantId, fromDate: b.fromDate, toDate: b.toDate,
  }))
  return weeklyRowsFromApi((res && res.data) || [])
}

/**
 * The server's week buckets carry weekStart/weekEnd (yyyy-MM-dd, IST) but no label, and the Week
 * column and the chart's axis both read `label`, so every week showed blank. Labels them the way the
 * in-browser breakdown does ("06 Oct – 12 Oct") and orders them oldest first, as that one does.
 */
export function weeklyRowsFromApi(buckets) {
  const dateOf = (ymd) => {
    const [y, m, d] = String(ymd || '').split('-').map(Number)
    return y && m && d ? new Date(y, m - 1, d) : null
  }
  const short = (date) => date.toLocaleDateString('en-IN', {day: '2-digit', month: 'short'})
  return (Array.isArray(buckets) ? buckets : [])
    .map((w) => {
      // The server's weeks are rolling 7-day windows ending "now", so weekStart is the previous week's
      // weekEnd and adjacent labels shared a day ("04 Oct – 11 Oct", "11 Oct – 18 Oct"). Label each as
      // the seven days ending on its weekEnd instead.
      const end = dateOf(w.weekEnd)
      const start = end ? new Date(end.getFullYear(), end.getMonth(), end.getDate() - 6) : dateOf(w.weekStart)
      const label = start && end ? `${short(start)} – ${short(end)}` : (start ? short(start) : `Week ${w.weekIndex}`)
      return {...w, label}
    })
    .sort((a, b) => (b.weekIndex || 0) - (a.weekIndex || 0))
}

/** Recovery Analytics: the per-courier split. */
export async function fetchCourierAnalytics(params) {
  const b = withMerchant(toListBody(params))
  const res = await getGeneric(`${apiConstants.WD_V2_BASE}/analytics/courier`, dropEmpty({
    merchantId: b.merchantId, fromDate: b.fromDate, toDate: b.toDate,
  }))
  return (res && res.data) || []
}

/**
 * SKU Weight Intelligence, aggregated server-side and paged.
 *
 * Takes the same body as the list so the table answers "top SKUs under the filters I can see",
 * rather than disagreeing with the grid beside it.
 */
export async function fetchSkuAnalytics(params) {
  const res = await postGeneric(`${apiConstants.WD_V2_BASE}/analytics/sku`, withMerchant(toListBody(params)))
  const body = (res && res.data) || {}
  return {rows: body.rows || [], remaining: body.remaining || 0, page: body.page || 0, size: body.size || 0}
}

function dropEmpty(o) {
  const out = {}
  Object.keys(o).forEach((k) => { if (o[k] !== undefined && o[k] !== null && o[k] !== '') out[k] = o[k] })
  return out
}

export async function fetchDispute(id) {
  const res = await getGeneric(`${apiConstants.WD_V2_BASE}/${id}`)
  return mapRowFromApi(res && res.data)
}

/** Pulls the server's own sentence out of an axios failure, or falls back. */
function serverMessage(err, fallback) {
  const data = err && err.response && err.response.data
  if (data) {
    if (typeof data === 'string' && data) return data
    if (data.error) return data.error
    if (data.message) return data.message
  }
  return (err && err.message) || fallback
}

/** ActionResult (ok/reason/row) → mapped row, or a thrown Error carrying the reason. */
function unwrapActionResult(result, fallback) {
  if (!result || !result.ok) {
    throw new Error((result && result.reason) || fallback)
  }
  return result.row ? mapRowFromApi(result.row) : null
}

export async function apiAccept(row, remark) {
  let res
  try {
    const text = remark && String(remark).trim()
    res = await postGeneric(`${apiConstants.WD_V2_BASE}/${row.id}/accept`, text ? {remark: text} : {})
  } catch (err) {
    throw new Error(serverMessage(err, `Accept failed for AWB ${row.awb}`))
  }
  return unwrapActionResult(res && res.data, `Accept failed for AWB ${row.awb}`)
}

export async function apiDispute(row, remark) {
  let res
  try {
    const text = remark && String(remark).trim()
    res = await postGeneric(`${apiConstants.WD_V2_BASE}/${row.id}/dispute`, text ? {remark: text} : {})
  } catch (err) {
    throw new Error(serverMessage(err, `Dispute failed for AWB ${row.awb}`))
  }
  return unwrapActionResult(res && res.data, `Dispute failed for AWB ${row.awb}`)
}

/**
 * Bulk accept. Resolves to the rows that actually changed — a row the server
 * refused is skipped, mirroring the local acceptMany's skip-not-abort contract.
 */
export async function apiAcceptBulk(rows, remark) {
  const list = rows || []
  if (!list.length) return []
  let res
  try {
    const text = remark && String(remark).trim()
    res = await postGeneric(`${apiConstants.WD_V2_BASE}/accept-bulk`, text ? {ids: list.map((r) => r.id), remark: text} : {ids: list.map((r) => r.id)})
  } catch (err) {
    throw new Error(serverMessage(err, 'Bulk accept failed'))
  }
  const results = (res && res.data) || []
  return results
    .filter((r) => r && r.ok && r.row)
    .map((r) => mapRowFromApi(r.row))
}

/**
 * Real per-SKU shipment volumes from the weight ledger (D17: statistics run on
 * the last ~200 shipments). Resolves to {sku: count}; a SKU with no ledger rows
 * simply has no key. Replaces state/analytics.js's fabricated placeholder in
 * live mode — see skuBreakdown's shipmentCounts parameter.
 */
export async function apiSkuShipmentCounts(skus, merchantId) {
  const list = Array.from(new Set((skus || []).filter(Boolean)))
  if (!list.length) return {}
  // SKU reads are per merchant on the server. A merchant login needs no merchantId; a
  // Prozo-admin login (cross-merchant scope) is refused without one, so the caller passes
  // the merchant the rows belong to whenever they all share one.
  const scope = merchantId || actingMerchantOid()
  const body = scope ? {skus: list, merchantId: scope} : {skus: list}
  const res = await postGeneric(`${apiConstants.WD_V2_BASE}/sku/shipment-counts`, body)
  return (res && res.data) || {}
}

/** Last-N shipments of one SKU from the ledger (D17: the drawer's history list shows 50). */
export async function apiSkuHistory(sku, limit, merchantId) {
  const params = {}
  if (limit) params.limit = limit
  const scope = merchantId || actingMerchantOid()
  if (scope) params.merchantId = scope
  const res = await getGeneric(`${apiConstants.WD_V2_BASE}/sku/${encodeURIComponent(sku)}/history`, params)
  return (res && res.data) || []
}

// EvidenceIntake's slot ids → the multipart part names the evidence endpoint
// takes (weightv2/WeightV2Controller#addEvidence).
const SLOT_TO_PART = {
  length: 'image_len',
  width: 'image_wid',
  height: 'image_hgt',
  scale: 'image_scl',
  label: 'image_lbl',
}

/**
 * Uploads one row's evidence and resolves to the UPDATED row the server
 * returns (counts, claimed figures and links all come back recomputed).
 * `payload` is EvidenceIntake's submit payload; only entries that are real
 * File objects are sent — the demo harness's boolean slot flags carry no
 * bytes and are skipped.
 */
export async function apiSubmitEvidence(rowId, payload) {
  const form = new FormData()
  const files = (payload && payload.files) || {}
  const images = files.images || {}
  Object.keys(SLOT_TO_PART).forEach((slot) => {
    const f = images[slot]
    if (typeof File !== 'undefined' && f instanceof File) form.append(SLOT_TO_PART[slot], f)
  })
  if (typeof File !== 'undefined' && files.video instanceof File) form.append('video', files.video)

  const dim = payload && payload.dim
  if (dim && Number(dim.l) > 0 && Number(dim.b) > 0 && Number(dim.h) > 0) {
    form.append('dimL', String(dim.l))
    form.append('dimB', String(dim.b))
    form.append('dimH', String(dim.h))
  }
  if (payload && Number(payload.weightKg) > 0) form.append('claimedDeadKg', String(payload.weightKg))
  if (payload && payload.productCategory) form.append('productCategory', payload.productCategory)
  if (payload && payload.productUrl) form.append('productUrl', payload.productUrl)

  let res
  try {
    res = await postMultipart(`${apiConstants.WD_V2_BASE}/${rowId}/evidence`, form)
  } catch (err) {
    // The endpoint's 400 carries per-file reasons in `errors`; the first one
    // is the sentence worth surfacing.
    const data = err && err.response && err.response.data
    const first = data && Array.isArray(data.errors) && data.errors.length ? data.errors[0] : null
    throw new Error(first || serverMessage(err, 'Evidence upload failed'))
  }
  return mapRowFromApi(res && res.data)
}
